import React, { useCallback, useEffect, useState } from 'react'

import { AddCircleOutline } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import Table from '../../../../shared/components/Table/Table.component'
import Tabs from '../../../../shared/components/Tabs/Tabs.component'

import env from '../../../../networkRequests/apiClient/env.config'
import { getDiscountCodesForAdmin } from '../../../../redux/states/admin/payments/discountCodes/getDiscountCodesForAdmin/getDiscountCodesForAdmin.slice'
import {
  DiscountCode,
  GetDiscountCodesForAdminRequestPayload
} from '../../../../redux/states/admin/payments/discountCodes/getDiscountCodesForAdmin/getDiscountCodesForAdmin.types'
import { RootState } from '../../../../redux/store/store.types'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import { useDiscountCodeModal } from './DiscountCodeModal'
import { discountCodesTableColumns } from './DiscountCodes.dictionary'
import styles from './DiscountCodes.styles'
import { DiscountCodesProps } from './DiscountCodes.types'

const DiscountCodes: React.FC<DiscountCodesProps> = (props) => {
  const { setDiscountCodesTabIndex, discountCodesTabIndex, searchQuery } = props

  const [searchParams, setSearchParams] = useSearchParams()
  const [activeDiscountCodesPage, setActiveDiscountCodesPage] = useState(1)
  const [expiredDiscountCodesPage, setExpiredDiscountCodesPage] = useState(1)
  const [activeDiscountCodesTotalPages, setActiveDiscountCodesTotalPages] = useState(1)
  const [expiredDiscountCodesTotalPages, setExpiredDiscountCodesTotalPages] = useState(1)
  const [activeDiscountCodesTotalRecords, setActiveDiscountCodesTotalRecords] = useState(0)
  const [expiredDiscountCodesTotalRecords, setExpiredDiscountCodesTotalRecords] = useState(0)
  const [activeDiscountCodesData, setActiveDiscountCodesData] = useState<DiscountCode[]>([])
  const [expiredDiscountCodesData, setExpiredDiscountCodesData] = useState<DiscountCode[]>([])

  const pageSize = env.DEFAULT_PAGE_SIZE
  const dispatch = useAppDispatch()

  const {
    loading: discountCodesLoading,
    success: discountCodesSuccess,
    error: discountCodesError
  } = useSelector((state: RootState) => state.getDiscountCodesForAdmin)

  const handleSuccess = () => {
    // Refresh the discount codes list
    handleGetDiscountCodes()
  }

  const { showModal, modalComponent } = useDiscountCodeModal(handleSuccess)

  const handleGetDiscountCodes = useCallback(
    (page: number = 1, size: number = pageSize, status?: string) => {
      const payload: GetDiscountCodesForAdminRequestPayload = {
        request: {
          page,
          limit: size,
          search: searchQuery,
          status
        }
      }

      dispatch(getDiscountCodesForAdmin(payload))
    },
    [dispatch, searchQuery, pageSize]
  )

  const handleGetActiveDiscountCodes = useCallback(
    (page: number = 1, size: number = pageSize) => {
      handleGetDiscountCodes(page, size, 'active')
    },
    [handleGetDiscountCodes, pageSize]
  )

  const handleGetExpiredDiscountCodes = useCallback(
    (page: number = 1, size: number = pageSize) => {
      handleGetDiscountCodes(page, size, 'expired')
    },
    [handleGetDiscountCodes, pageSize]
  )

  // Handle success response and update data based on current tab
  useEffect(() => {
    if (discountCodesSuccess?.data) {
      const { currentPage, totalPages, totalRecords, records } = discountCodesSuccess.data

      if (discountCodesTabIndex === 0) {
        // Active tab
        setActiveDiscountCodesTotalPages(totalPages)
        setActiveDiscountCodesTotalRecords(totalRecords)
        setActiveDiscountCodesPage(currentPage)
        setActiveDiscountCodesData(records)
      } else {
        // Expired tab
        setExpiredDiscountCodesTotalPages(totalPages)
        setExpiredDiscountCodesTotalRecords(totalRecords)
        setExpiredDiscountCodesPage(currentPage)
        setExpiredDiscountCodesData(records)
      }
    }
  }, [discountCodesSuccess, discountCodesTabIndex])

  // Load data when tab changes
  useEffect(() => {
    if (discountCodesTabIndex === 0) {
      handleGetActiveDiscountCodes()
    } else {
      handleGetExpiredDiscountCodes()
    }
  }, [discountCodesTabIndex, handleGetActiveDiscountCodes, handleGetExpiredDiscountCodes])

  // Initialize tab from URL query parameter and sync with URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')

    if (tabFromUrl === 'expired' && discountCodesTabIndex !== 1) {
      setDiscountCodesTabIndex(1)
    } else if (tabFromUrl === 'active' && discountCodesTabIndex !== 0) {
      setDiscountCodesTabIndex(0)
    } else if (!tabFromUrl && discountCodesTabIndex !== 0) {
      // Default to active tab if no tab parameter
      setDiscountCodesTabIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]) // Only react to URL changes, not tab state changes to prevent infinite loop

  // Reset data when search query changes
  useEffect(() => {
    setActiveDiscountCodesPage(1)
    setExpiredDiscountCodesPage(1)
    setActiveDiscountCodesData([])
    setExpiredDiscountCodesData([])
  }, [searchQuery])

  const handleTabChange = (newValue: number) => {
    setDiscountCodesTabIndex(newValue)

    // Update URL query parameter
    const newSearchParams = new URLSearchParams(searchParams)
    if (newValue === 0) {
      newSearchParams.set('tab', 'active')
    } else {
      newSearchParams.set('tab', 'expired')
    }
    setSearchParams(newSearchParams)
  }

  const getCreateButton = () => (
    <Box sx={styles.createButtonContainer}>
      <Button variant="contained" startIcon={<AddCircleOutline />} onClick={showModal}>
        Create Discount Code
      </Button>
    </Box>
  )

  const tabs = [
    {
      label: 'Active',
      content: (
        <Table
          columns={discountCodesTableColumns}
          data={activeDiscountCodesData}
          pagination={{
            currentPage: activeDiscountCodesPage,
            totalPages: activeDiscountCodesTotalPages,
            totalRecords: activeDiscountCodesTotalRecords,
            onNext: () => handleGetActiveDiscountCodes(activeDiscountCodesPage + 1),
            onPrevious: () => handleGetActiveDiscountCodes(activeDiscountCodesPage - 1)
          }}
          initialLoading={discountCodesLoading}
          error={!!discountCodesError}
          onRetry={() => handleGetActiveDiscountCodes()}
          inPage
        />
      )
    },
    {
      label: 'Expired',
      content: (
        <Table
          columns={discountCodesTableColumns}
          data={expiredDiscountCodesData}
          pagination={{
            currentPage: expiredDiscountCodesPage,
            totalPages: expiredDiscountCodesTotalPages,
            totalRecords: expiredDiscountCodesTotalRecords,
            onNext: () => handleGetExpiredDiscountCodes(expiredDiscountCodesPage + 1),
            onPrevious: () => handleGetExpiredDiscountCodes(expiredDiscountCodesPage - 1)
          }}
          initialLoading={discountCodesLoading}
          error={!!discountCodesError}
          onRetry={() => handleGetExpiredDiscountCodes()}
          inPage
        />
      )
    }
  ]

  return (
    <Box sx={styles.container}>
      {getCreateButton()}

      <Tabs tabs={tabs} value={discountCodesTabIndex} onChange={handleTabChange} />

      {modalComponent}
    </Box>
  )
}

export default DiscountCodes
